import type { BuiltInTool, BuiltInToolError, JsonSchema } from './types'

/**
 * #1435: create an enterprise's own task, and publish a new version of one, for a person's own agent over the API
 * and the OS MCP server. One tool per route: `POST /v1/tasks` and `POST /v1/tasks/:id/versions`, both under
 * `tasks:write`. Publishing to the catalog is `visibility` on the version, which also needs `catalog:write`.
 *
 * Neither lists `run` or `chat` among its callers, so a task that declares one is refused when it is saved
 * (`tool_not_run_callable`) and neither is re-pointed at a run-scoped endpoint. A built-in task is OS code,
 * shipped with a release, and both routes refuse to create or edit one.
 *
 * #1858: the built-in names stay undeclarable. A task may reach the same writes as a bound connection to the OS's
 * own MCP server (`zerohuman.os.create_task`), and then only with a gate naming each write that server serves; a run
 * cannot publish the task it is running (`self_publish_refused`).
 */

const TASK_SLUG_TAKEN: BuiltInToolError = {
    code: 'task_slug_taken',
    when: 'This enterprise already has a task with that slug.',
    handling: 'tool_error',
}

const TASK_TEAM_REMOVED: BuiltInToolError = {
    code: 'task_team_removed',
    when: 'The call names a team for the task. A task belongs to a role, not a team: a team runs the tasks of the roles its members hold. Seat a holder of the task’s role on the team instead.',
    handling: 'tool_error',
}

const INVALID_NAME: BuiltInToolError = {
    code: 'invalid_name',
    when: '`name` is empty, or longer than 200 characters.',
    handling: 'tool_error',
}

const INVALID_SLUG: BuiltInToolError = {
    code: 'invalid_slug',
    when: 'The slug is empty once cleaned, or longer than 80 characters.',
    handling: 'tool_error',
}

const INVALID_TASK_DEFINITION: BuiltInToolError = {
    code: 'invalid_task_definition',
    when: 'The definition fails a check: a deterministic task with tools, a merge or mail or plan gate that is missing, a successor the rules refuse, a call limit on a tool the task does not declare (call_limit_tool_not_declared).',
    handling: 'tool_error',
}

const DEFINITION_ISSUES: BuiltInToolError = {
    code: 'definition_issues',
    when: 'A create over the OS’s own MCP server whose definition would stop or loop its own pipeline: it carries outcome_not_routed (named with the word), self_loop_unconditional or asks_ceo_without_resume. Nothing is saved, and issues lists each one.',
    handling: 'tool_error',
}

const CALL_LIMITS_INVALID: BuiltInToolError = {
    code: 'call_limits_invalid',
    when: '`callLimits` is not a list of up to 16 objects. The refusal names the fault instead when one limit is wrong: call_limit_tool_invalid (not one tool by its exact name), call_limit_match_invalid:<tool> (not a gate-shaped match) or call_limit_per_run_invalid:<tool> (not a whole number of at least 1).',
    handling: 'tool_error',
}

const SERIALIZE_ON_INVALID: BuiltInToolError = {
    code: 'serialize_on_invalid',
    when: '`serializeOn` is not a list of up to 8 input keys.',
    handling: 'tool_error',
}

const TOOL_ENTRY_UNREADABLE: BuiltInToolError = {
    code: 'tool_entry_unreadable',
    when: 'A `tools` entry names no tool.',
    handling: 'tool_error',
}

const LLM_MODEL_NOT_IN_CATALOGUE: BuiltInToolError = {
    code: 'llm_model_not_in_catalogue',
    when: '`spend.model` is not a model a connected provider offers.',
    handling: 'tool_error',
}

const TASK_NOT_FOUND: BuiltInToolError = {
    code: 'task_not_found',
    when: "`id` names no task in this enterprise, another enterprise's included.",
    handling: 'tool_error',
}

const BASE_VERSION_REQUIRED: BuiltInToolError = {
    code: 'base_version_required',
    when: '`baseVersionId` is missing or blank. Read the task and send its currentVersionId.',
    handling: 'tool_error',
}

const TASK_VERSION_CONFLICT: BuiltInToolError = {
    code: 'task_version_conflict',
    when: 'Someone published a newer version than `baseVersionId`. The body names `currentVersionId` and `currentVersion`.',
    handling: 'tool_error',
}

const TASK_DEFINITION_READ_ONLY: BuiltInToolError = {
    code: 'task_definition_read_only',
    when: "The task is a clone: its definition belongs to the original's owner.",
    handling: 'tool_error',
}

const OS_TASK_IMMUTABLE: BuiltInToolError = {
    code: 'os_task_immutable',
    when: "The task is one of the OS's own: the same in every enterprise, changed only with a release.",
    handling: 'tool_error',
}

const SKILL_MD_REQUIRED: BuiltInToolError = {
    code: 'skillMd_required',
    when: 'The skill would be empty.',
    handling: 'tool_error',
}

const text = (description: string): JsonSchema => ({ type: 'string', description })

const textList = (description: string): JsonSchema => ({
    type: 'array',
    description,
    items: { type: 'string' },
})

const objectList = (description: string): JsonSchema => ({
    type: 'array',
    description,
    items: { type: 'object' },
})

const opaque = (description: string): JsonSchema => ({ type: 'object', description })

/**
 * The definition fields both writes take. On create, a field left out gets the API's default. On publish, a field
 * left out keeps the current version, and `name`, `slug`, `assigneeRole`, `serializeOn`, `status` and
 * `kind` change the task row at once rather than the version.
 */
function definitionFields(): Record<string, JsonSchema> {
    return {
        name: text('The task’s name, trimmed, at most 200 characters.'),
        slug: text('A slug of letters, digits and hyphens, at most 80 characters.'),
        kind: {
            type: 'string',
            enum: ['llm', 'deterministic'],
            description: 'llm or deterministic. A deterministic task has no tools.',
        },
        status: {
            type: 'string',
            enum: ['active', 'deprecated'],
            description: 'active or deprecated.',
        },
        assigneeRole: text(
            'The role whose holders run the task, by the role’s slug. A task is given to a role, never a team: every holder of the role runs it, on whatever team they sit, and a team runs the tasks of the roles its members hold. assigneeMemberId is refused.',
        ),
        goal: text('What the task is for, in one or two sentences.'),
        metric: opaque('How the task is judged, as { text }.'),
        guardrails: textList('Sentences the task must hold to. Each item is plain text.'),
        tools: textList(
            'The tools the task may call. Each item is a tool name, as a string, such as "acme.merge_pull_request". An entry that is an object, including one carrying its own gates or marked optional, cannot be sent through this tool: sending tools replaces them all, so leave tools out to keep the current ones.',
        ),
        inputs: objectList(
            'The inputs, as { key, label, type, required?, configuration?, requiredGroup?, entries? }. type is string, number, boolean, link or file. configuration marks persistent enterprise setup. requiredGroup requires at least one input in the group; every member must agree on configuration. A string map may declare entries: { keyInput, separator }: comma-separated key/value entries must cover the resolved keyInput at each start. Missing required configuration prevents a run from starting.',
        ),
        gates: objectList(
            'Gates on tools that reach outside. Each object is { tool, owner, approval }. On create, leave it out and the task is given the default CEO gates; on publish, leave it out and the current gates stay. Send [] to declare none, which is refused when a tool still requires a gate.',
        ),
        spend: opaque('Spend limits, as { moneyUsdPerRun, maxModelTier, model }. model is an exact model id.'),
        workspace: opaque(
            "The clone the task may change, as { branch, onDefaultBranch, writeAllow, rewriteAllow, writeDeny, appendOnly, pushAllow, requireFiles, credentials }. Each list is strings but `credentials`, which is [{ tool, as, prop? }]: a bound tool's secret (or its custom prop) as the environment variable `as` of the run's repository commands; `onDefaultBranch: true` keeps the run on the default branch's tip, with no branch of its own and nothing to push; `pushAllow` lists the full ref globs (`refs/heads/main`, `refs/tags/v*`) a run may push with `workspace.git_push`'s `ref` besides its own branch, and needs `writeAllow`.",
        ),
        successors: objectList(
            'What runs after this task. Each object is { slug, on, when, delayMinutes, maxRunsPerIssue, escalate, verificationAnyOf }. on is succeeded, failed or terminal.',
        ),
        finishesOn: textList('Outcome names that finish the work. Each item is plain text, at most 16.'),
        toolSlots: objectList(
            'Tool slots the task’s tools may be named by in place of one vendor’s tool type, at most 16. Each object is { key, label, description?, actions? }: key reads like a tool type (codehost), label is what a person choosing a binding reads. A tool named "codehost" or "codehost.<tool>" is then filled per enterprise by the binding that lists codehost among the slots it fills, and runs as that binding’s tool type. actions is a list of { name, description? }, at most 32, the actions the task names under the key ("codehost.pr.merge"); the filling binding maps each to one of its own tools, so the task names no vendor’s tool.',
        ),
        callLimits: objectList(
            'How often one run may make a call, at most 16. Each object is { tool, match?, perRun }: tool is one tool the task’s tools reach, by its exact name ("acme.issue_write"); match names payload fields the call must carry to count, as a gate’s match does ({ "method": "create" }); perRun is a whole number of at least 1. A call past the limit is not sent, and the run reads call_limit_reached instead.',
        ),
        mailboxAction: opaque(
            'Where a mailbox offers this task as a one-click button, as { on, label }. on is mailbox (a button above the mailbox’s list; the run starts with no input of the OS’s) or mail (a button on one open message; the run starts with that message’s id as its emailId input, so the task has to declare an input with that key). label is the button’s words, at most 40 characters. A public template that declares one is offered to clone on inboxes that have no such task yet. Leave it out to offer none.',
        ),
        skillMd: text('The skill text, markdown, telling the model how to do the job.'),
        contextPrefixes: textList('Key prefixes loaded into input.context when a run starts. Each item is plain text.'),
        trainGrants: objectList(
            'Team or enterprise notes a run of this task may write. Each object is { layer, eventIdPrefix }, layer team or enterprise. A team note goes to each team the running member sits on or leads.',
        ),
        overlapPolicy: {
            type: 'string',
            enum: ['skip', 'os'],
            description: 'skip waits behind the member’s other work. os runs beside it.',
        },
        serializeOn: textList(
            'Input keys that partition this task’s work into lanes, up to 8. Each item is plain text.',
        ),
    }
}

const CREATE_ERRORS: readonly BuiltInToolError[] = [
    INVALID_NAME,
    INVALID_SLUG,
    TASK_SLUG_TAKEN,
    TASK_TEAM_REMOVED,
    INVALID_TASK_DEFINITION,
    DEFINITION_ISSUES,
    CALL_LIMITS_INVALID,
    SERIALIZE_ON_INVALID,
    TOOL_ENTRY_UNREADABLE,
    LLM_MODEL_NOT_IN_CATALOGUE,
    OS_TASK_IMMUTABLE,
]

const PUBLISH_ERRORS: readonly BuiltInToolError[] = [
    BASE_VERSION_REQUIRED,
    TASK_VERSION_CONFLICT,
    TASK_NOT_FOUND,
    TASK_DEFINITION_READ_ONLY,
    OS_TASK_IMMUTABLE,
    SKILL_MD_REQUIRED,
    INVALID_NAME,
    INVALID_SLUG,
    TASK_SLUG_TAKEN,
    TASK_TEAM_REMOVED,
    INVALID_TASK_DEFINITION,
    CALL_LIMITS_INVALID,
    SERIALIZE_ON_INVALID,
    TOOL_ENTRY_UNREADABLE,
    LLM_MODEL_NOT_IN_CATALOGUE,
]

export const OS_TASK_WRITE_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.create_task',
        family: 'os.org',
        summary: 'Create one of the enterprise’s own tasks.',
        description:
            'Create one of the enterprise’s own tasks. Pass name, goal, metric ({ text }) and skillMd. A slug left ' +
            'out is made from the name. kind defaults to llm, and status and runStatus default to active. Leave gates ' +
            'out and the task is given the default CEO gates; send gates as [] to declare none. A task that declares ' +
            'this tool is refused when it is saved. A task reaches it only through a bound connection to the OS’s own ' +
            'MCP server, with a gate naming the tool, as with every write that server serves. The OS’s built-in tasks ' +
            'are created with a release, never through this tool, and a slug one of them holds is reserved. Give the ' +
            'task to a role with assigneeRole: a task belongs to a role, not a team, and a team runs it by seating a ' +
            'holder of that role. Over this tool the create is refused with definition_issues, and nothing is saved, ' +
            'while the definition carries a routing issue: outcome_not_routed, named with the word (the skill tells ' +
            'a run to end on an outcome that no successor, finishesOn entry or plain success handles), ' +
            'self_loop_unconditional (a successor back to its own slug with no delayMinutes, other than ceo_approved ' +
            'or ceo_changes_requested), or asks_ceo_without_resume (it asks the CEO, does more work after the answer, ' +
            'and has no ceo_approved successor back to its own slug). Fix each and create it again. The response is ' +
            'the task as created, including id and currentVersionId.',
        inputSchema: {
            type: 'object',
            properties: {
                ...definitionFields(),
                runStatus: {
                    type: 'string',
                    enum: ['active', 'disabled'],
                    description: 'active, the default, or disabled. disabled keeps runs from starting.',
                },
            },
            required: ['name', 'goal', 'metric', 'skillMd'],
            additionalProperties: false,
        },
        returns: 'The task as created: `id`, `slug`, `currentVersionId`, `currentVersion`, `skillMd`.',
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'unsafe',
        retryNote:
            'Every call creates a task. A repeat with the same slug is refused (task_slug_taken) rather than returning the first.',
        errors: CREATE_ERRORS,
        api: { method: 'POST', path: '/v1/tasks', scope: 'tasks:write' },
        examples: [
            {
                input: {
                    name: 'Triage new issues',
                    goal: 'Label a new issue and hand it to the right pipeline.',
                    metric: { text: 'Each new issue is labelled or handed on within a day.' },
                    skillMd: '# Triage new issues\n\nRead the issue and label it.\n',
                    assigneeRole: 'engineer',
                },
            },
        ],
        public: true,
    },
    {
        name: 'os.publish_task_version',
        family: 'os.org',
        summary: 'Publish a new version of one of the enterprise’s tasks, naming the version it was read from.',
        description:
            'Publish a new version of one of the enterprise’s tasks. Pass id and baseVersionId, the currentVersionId ' +
            'from os.read_task_definition. Send only the fields that change: a field left out keeps its current value. ' +
            'Sending null does not clear a field through this tool, because a null argument is dropped. Clear inputs, ' +
            'workspace, successors, finishesOn, toolSlots, callLimits or mailboxAction over HTTP by sending null. name, slug, ' +
            'assigneeRole, serializeOn, status and kind change the task at once and are not part of the version. A save ' +
            'that changes nothing returns the current version with unchanged true. visibility public publishes the ' +
            'version to the catalog, listed on the task’s role when the enterprise has published that role, and private withdraws ' +
            'it and records the task as left out; either also needs catalog:write. Once the task is in the catalog, every ' +
            'version published after goes there too without visibility; a version the catalog refuses is still saved, ' +
            'and catalogPublication says why the catalog kept its version. A task that declares this tool is ' +
            'refused when it is saved. A task reaches it only through a bound connection to the OS’s own MCP server, ' +
            'with a gate naming the tool, as with every write that server serves, and a run cannot publish the task ' +
            'it is running (self_publish_refused). The OS’s built-in tasks are not edited this way. Give the task to ' +
            'a role with assigneeRole; a task has no team. A version that carries a routing issue is still saved, and ' +
            'definitionIssues lists each as a warning: outcome_not_routed, named with the word (the skill ends on ' +
            'a word no successor or finishesOn entry handles), self_loop_unconditional (a successor back to its own slug with ' +
            'no delayMinutes) or asks_ceo_without_resume (it asks the CEO and works on after the answer with no ' +
            'ceo_approved successor back to itself). The response is the version, and its id is the next baseVersionId.',
        inputSchema: {
            type: 'object',
            properties: {
                id: text('The task, by its id from os.list_task_definitions.'),
                baseVersionId: text('The currentVersionId this change was read from.'),
                ...definitionFields(),
                allowSelfReaction: {
                    type: 'boolean',
                    description:
                        'Kept for versions written before it was retired: the OS no longer reads it, and a reaction is a call like any other. Leave it out.',
                },
                visibility: {
                    type: 'string',
                    enum: ['public', 'private'],
                    description:
                        'public publishes this version to the catalog, private withdraws it. Either also needs catalog:write. Left out, a task already in the catalog sends this version there too, and any other task changes only itself.',
                },
            },
            required: ['id', 'baseVersionId'],
            additionalProperties: false,
        },
        returns:
            'The version: `id`, `version`, the definition, and `unchanged` (`true` when the save decided nothing). For a task in the catalog, or a save with `visibility: public`, `catalogPublication`: the entry’s `id`, `version` and `sourceVersion` (the task version it holds), `updated` (whether this save reached the catalog) and `refusal` (`error` and `message` saying why the catalog kept its version, or null).',
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'unsafe',
        retryNote:
            'A repeat after a successful publish is refused (task_version_conflict): baseVersionId is then stale. Read the task again and send the new currentVersionId. A save that changes nothing returns the current version with unchanged true.',
        errors: PUBLISH_ERRORS,
        api: { method: 'POST', path: '/v1/tasks/:id/versions', scope: 'tasks:write' },
        examples: [
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000013',
                    baseVersionId: '6a1d2e3f-0000-4000-8000-000000000014',
                    guardrails: ['Never close an issue.'],
                },
            },
        ],
        public: true,
    },
]
